export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterS: number | null;

  constructor(status: number, code: string, message: string, retryAfterS: number | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfterS = retryAfterS;
  }
}

export type ApiInit = { method?: string; body?: unknown; signal?: AbortSignal };

/** Same-origin JSON request. The browser adds the Origin header the hub's CSRF check needs. */
export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const hasBody = init.body !== undefined;
  const res = await fetch(path, {
    method: init.method ?? "GET",
    credentials: "same-origin",
    ...(hasBody
      ? { headers: { "content-type": "application/json" }, body: JSON.stringify(init.body) }
      : {}),
    ...(init.signal ? { signal: init.signal } : {}),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const body = (data ?? {}) as { error?: unknown; message?: unknown };
    const retry = Number(res.headers.get("retry-after"));
    throw new ApiError(
      res.status,
      typeof body.error === "string" ? body.error : `http_${res.status}`,
      typeof body.message === "string" ? body.message : res.statusText || `HTTP ${res.status}`,
      Number.isFinite(retry) && retry > 0 ? retry : null,
    );
  }
  return data as T;
}
